// Typed client for the FabulaWiki API. Every wiki read passes a bookmark: the API (and the
// database behind it) only returns what is known up to that chapter.

import type {
  Article,
  Category,
  Changes,
  ChapterPage,
  Graph,
  Portal,
  Timeline,
} from "./wiki/types";

export type TokenProvider = () => Promise<string>;

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
  ) {
    super(message);
  }
}

export interface Me {
  id: string;
  email: string | null;
  display_name: string | null;
  status: "pending" | "approved" | "blocked";
  is_admin: boolean;
  credits_remaining: number;
  country: string | null;
}

/** An e-reader linked with a pairing code (Kobo with the KOReader plugin). */
export interface Device {
  id: string;
  name: string;
  created_at: string;
  last_used_at: string | null;
}

export interface PairingCode {
  code: string;
  expires_at: string;
}

export type JobStatus =
  | "queued"
  | "running"
  | "succeeded"
  | "failed"
  | "cancelled"
  | "budget_exceeded";

export interface Book {
  id: string;
  owner_id: string;
  title: string;
  authors: string[];
  language: string | null;
  num_chapters: number;
  visibility: string;
  job_id: string | null;
  job_status: JobStatus | null;
  job_stage: string | null;
  job_error: string | null;
  job_not_before: string | null;
  job_progress_done: number | null;
  job_progress_total: number | null;
  first_chapter_index: number | null;
  last_chapter_index: number | null;
  rights_basis: "private" | "public_domain" | "licensed";
  /** Who stated the basis: the owner (when publishing) or an administrator. */
  rights_basis_by: "owner" | "admin" | null;
  rights_confirmation_version: string | null;
  source_sha256: string | null;
  provenance: Partial<Provenance> & { acknowledged?: boolean; warnings?: string[] };
  public_domain: Partial<PublicDomain>;
  isbn: string | null;
  /** What identification found at upload (lookup + the file's own metadata); null for
   * books processed before October 2026 or when identification failed. */
  identification: Identification | null;
}

/** A book its owner has shared: readable by anyone logged in, without owner details. */
export type PublicBook = Pick<
  Book,
  | "id"
  | "title"
  | "authors"
  | "language"
  | "isbn"
  | "num_chapters"
  | "visibility"
  | "rights_basis"
  | "rights_basis_by"
  | "public_domain"
  | "first_chapter_index"
  | "last_chapter_index"
>;

export interface SharingResult {
  visibility: "private" | "public";
  rights_basis: Book["rights_basis"];
  warnings: string[];
}

export interface Identification {
  /** epub_isbn_lookup | epub_isbn_fallback | epub_passage_search | epub_metadata_only */
  method: string | null;
  language: string | null;
  selected: {
    isbn: string | null;
    isbn_10: string | null;
    title: string;
    authors: string[];
    language: string | null;
    publish_year: number | null;
    publisher?: string | null;
    source: string;
    confidence_score: number;
  } | null;
  epub: {
    title: string | null;
    authors: string[] | null;
    isbns: string[] | null;
    language: string | null;
    publisher?: string | null;
    date?: string | null;
    rights?: string | null;
  };
  identified_on: string;
}

export interface ProvenanceFinding {
  code: string;
  severity: "info" | "medium" | "high";
  message: string;
  detail: string | null;
}

export interface Provenance {
  version: string;
  sha256: string;
  risk: "none" | "medium" | "high";
  mode: "warn" | "block";
  findings: ProvenanceFinding[];
}

export interface PublicDomain {
  status: "likely_public_domain" | "protected" | "unknown";
  reasons: string[];
  free_from_year: number | null;
  checked_on: string;
  rule: string;
  note: string;
  authors: { name: string; matched_name: string | null; death_year: number | null }[];
}

export interface Estimate {
  provenance: Provenance;
  /** Why this upload would be refused (blocklist, policy), or null. */
  refused: string | null;
  warnings: string[];
  rights_statement_version: string;
  title: string | null;
  authors: string[];
  language: string | null;
  documents: number;
  characters: number;
  budget_usd: number;
  /** Processor (and its model) this estimate was priced for. */
  processor: string;
  model: string;
  estimate: {
    chapters: number;
    prompt_tokens: number;
    completion_tokens: number;
    cost_usd_no_cache: number;
    cost_usd_with_cache: number;
  };
}

export type EntityType = "character" | "place" | "object" | "group";

export interface Entity {
  entity_id: number;
  name: string;
  entity_type: EntityType;
  aliases: string[];
  first_seen_chapter: number;
  last_seen_chapter: number;
  mention_count: number;
}

export interface Fact {
  fact_id: number;
  chapter_index: number;
  fact_type: string;
  text: string;
  /** Only in the owner's review; readers get the chapter, never book text. */
  evidence_quote?: string;
}

export interface Relation {
  relation_id: number;
  chapter_index: number;
  relation_type: string;
  direction: "outgoing" | "incoming";
  other_entity_id: number;
  other_name: string;
  evidence_quote?: string;
}

export interface EntityDetail extends Entity {
  facts: Fact[];
  relations: Relation[];
}

/** Refresher before reading on: known entities that return in the next chapter. */
export interface StorySoFar {
  entities: (Entity & { facts: Fact[] })[];
  events: BookEvent[];
}

export interface RecapChapter {
  chapter_index: number;
  kind: "chapter" | "front_matter" | "back_matter";
  title: string;
  summary: string | null;
}

export interface BookEvent {
  event_id: number;
  chapter_index: number;
  title: string;
  description: string;
  evidence_quote?: string;
  entity_ids: number[];
}

export interface ReviewItem {
  item_type: "fact" | "relation" | "event";
  item_id: number;
  chapter_index: number;
  status: "verified" | "unverified" | "rejected" | "hidden";
  entity_ids: number[];
  text: string;
  evidence_quote: string;
}

export interface ReviewEntity {
  entity_id: number;
  name: string;
  entity_type: EntityType;
  aliases: string[];
  first_seen_chapter: number;
  mention_count: number;
  hidden: boolean;
  verified: number;
  not_verified: number;
}

export interface EditLogEntry {
  id: number;
  target: "fact" | "relation" | "event" | "entity";
  target_id: number;
  action: string;
  old_value: Record<string, unknown> | null;
  new_value: Record<string, unknown> | null;
  created_at: string;
}

export interface AdminUser {
  id: string;
  email: string | null;
  display_name: string | null;
  status: Me["status"];
  is_admin: boolean;
  created_at: string;
  credits_remaining: number;
  country: string | null;
  takedowns: number;
}

export type PolicyMode = "warn" | "block";

export interface Platform {
  extraction_enabled: boolean;
  off_peak_only: boolean;
  provenance_mode: PolicyMode;
  public_basis_mode: PolicyMode;
  country_mode: "off" | PolicyMode;
  allowed_countries: string[];
  monthly_budget_usd: string;
  month_spend_usd: string;
  month_remaining_usd: string;
  emergency_stop_env: boolean;
}

export interface UsageRow {
  day: string;
  model: string;
  prompt_tokens: number;
  cached_prompt_tokens: number;
  completion_tokens: number;
  cost_usd: string;
  calls: number;
}

export interface Job {
  id: string;
  status: JobStatus;
  stage: string | null;
  progress_done: number | null;
  progress_total: number | null;
  charge_credit: boolean;
  started_at: string | null;
  budget_usd: string;
  spent_usd: string;
  error: string | null;
  created_at: string;
  finished_at: string | null;
  source_deleted_at: string | null;
  artifacts_purged_at: string | null;
  has_source: boolean;
  has_chapters_artifact: boolean;
}

export interface Processor {
  id: string;
  name: string;
  region: string;
  available: boolean;
  /** The standard processor (DeepSeek); others are an explicit choice. */
  default?: boolean;
  note: string;
}

export interface LegalTexts {
  rights_statement: { version: string; title: string; items: string[] };
  terms_version: string;
  processors: Processor[];
}

export interface NoticeInput {
  kind: "copyright" | "privacy" | "other";
  reporter_name: string;
  reporter_email: string;
  reporter_role: "rights_holder" | "representative" | "other";
  organisation?: string;
  work_title: string;
  work_author?: string;
  isbn?: string;
  location?: string;
  explanation: string;
  good_faith: boolean;
  accurate: boolean;
}

export interface Notice extends NoticeInput {
  id: string;
  created_at: string;
  status: "open" | "actioned" | "rejected";
  decision: string | null;
  decided_at: string | null;
  books_removed: number;
}

export interface AdminBookHit {
  id: string;
  owner_id: string;
  owner_email: string | null;
  title: string;
  authors: string[];
  isbn: string | null;
  source_sha256: string | null;
  visibility: string;
  created_at: string;
  rights_basis: Book["rights_basis"];
  provenance: Book["provenance"];
  public_domain: Book["public_domain"];
}

export interface ContentBlock {
  id: number;
  kind: "sha256" | "isbn";
  value: string;
  reason: string | null;
  created_at: string;
}

export interface Takedown {
  id: number;
  title: string;
  reason: string;
  created_at: string;
}

export interface UploadOptions {
  rightsVersion: string;
  provenanceAcknowledged: boolean;
  processor: string;
  /** ISBN chosen from the suggestions; omitted = identification decides. */
  isbn?: string;
}

export interface IsbnSuggestion {
  isbn: string;
  isbn_10: string | null;
  title: string;
  authors: string[];
  publisher: string | null;
  publish_year: number | null;
  language: string | null;
  /** epub_metadata = in the file, not confirmed by a lookup */
  source: string;
  confidence_score: number;
}

export interface PublicConfig {
  auth_mode: "auth0" | "dev";
  auth0_domain: string | null;
  auth0_audience: string | null;
  auth0_client_id: string | null;
  wiki_url: string;
  /** Origin of the API when the web app is served by its own container ("" = same origin). */
  api_url?: string;
}

async function publicJson<T>(url: string, init?: RequestInit): Promise<T> {
  const r = await fetch(url, init);
  const data = await r.json().catch(() => ({}));
  if (!r.ok) {
    const detail = typeof data.detail === "string" ? data.detail : r.statusText;
    throw new ApiError(r.status, detail);
  }
  return data as T;
}

/** No login needed: legal texts (for the terms page) and the notice form. */
export const publicApi = {
  legal: (apiBase: string) => publicJson<LegalTexts>(`${apiBase}/legal`),
  notice: (apiBase: string, body: NoticeInput) =>
    publicJson<{ id: string; created_at: string }>(`${apiBase}/legal/notices`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    }),
};

/** Served by the web container (nginx, from its environment) or by the API itself. */
export async function fetchConfig(): Promise<PublicConfig> {
  const r = await fetch("/config");
  if (!r.ok) throw new ApiError(r.status, "configuratie kon niet worden geladen");
  return r.json();
}

export class Api {
  /** wikiBase / apiBase: origins of the separately scaled services ("" = same origin). */
  constructor(
    private token: TokenProvider,
    private wikiBase = "",
    private apiBase = "",
  ) {}

  private async request<T>(
    method: string,
    path: string,
    body?: unknown,
    base = this.apiBase,
  ): Promise<T> {
    const headers: Record<string, string> = {
      Authorization: `Bearer ${await this.token()}`,
    };
    let payload: BodyInit | undefined;
    if (body instanceof FormData) payload = body;
    else if (body !== undefined) {
      headers["Content-Type"] = "application/json";
      payload = JSON.stringify(body);
    }
    const r = await fetch(`${base}${path}`, { method, headers, body: payload });
    if (r.status === 204) return undefined as T;
    const data = await r.json().catch(() => ({}));
    if (!r.ok) {
      const detail = typeof data.detail === "string" ? data.detail : r.statusText;
      throw new ApiError(r.status, detail);
    }
    return data as T;
  }

  me = () => this.request<Me>("GET", "/me");
  updateMe = (body: { country: string | null }) => this.request<Me>("PATCH", "/me", body);
  myTakedowns = () => this.request<Takedown[]>("GET", "/me/takedowns");
  legal = () => this.request<LegalTexts>("GET", "/legal");
  exportMe = () => this.request<unknown>("GET", "/me/export");
  deleteMe = () => this.request<void>("DELETE", "/me?confirm=true");
  devices = () => this.request<Device[]>("GET", "/me/devices");
  pairingCode = () => this.request<PairingCode>("POST", "/me/devices/pairing-code");
  unlinkDevice = (id: string) => this.request<void>("DELETE", `/me/devices/${id}`);
  books = () => this.request<Book[]>("GET", "/books");
  book = (id: string) => this.request<Book>("GET", `/books/${id}`);
  publicBooks = () => this.request<PublicBook[]>("GET", "/books/public");
  setSharing = (
    id: string,
    body: { visibility: "private" | "public"; rights_basis?: "public_domain" | "licensed"; confirmed?: boolean },
  ) => this.request<SharingResult>("PATCH", `/books/${id}/sharing`, body);
  jobs = (id: string) => this.request<Job[]>("GET", `/books/${id}/jobs`);
  deleteBook = (id: string) => this.request<void>("DELETE", `/books/${id}`);

  estimate(file: File, processor?: string) {
    const form = new FormData();
    form.append("file", file);
    if (processor) form.append("processor_preference", processor);
    return this.request<Estimate>("POST", "/books/estimate", form);
  }

  /** ISBN shortlist from identification (lookups; can take a few seconds). */
  isbnSuggestions(file: File) {
    const form = new FormData();
    form.append("file", file);
    return this.request<{ suggestions: IsbnSuggestion[]; complete: boolean }>(
      "POST",
      "/books/isbn-suggestions",
      form,
    );
  }

  upload(file: File, opts: UploadOptions) {
    const form = new FormData();
    form.append("file", file);
    form.append("rights_confirmed", "true");
    form.append("rights_version", opts.rightsVersion);
    form.append("provenance_acknowledged", String(opts.provenanceAcknowledged));
    if (opts.processor) form.append("processor_preference", opts.processor);
    if (opts.isbn) form.append("isbn", opts.isbn);
    return this.request<{ book_id: string; job_id: string }>("POST", "/books", form);
  }

  /** Offer the original file again: reload without LLM cost (the text was deleted). */
  reofferSource(bookId: string, file: File) {
    const form = new FormData();
    form.append("file", file);
    return this.request<{ job_id: string }>("POST", `/books/${bookId}/source`, form);
  }

  recap = (id: string, bookmark: number) =>
    this.request<RecapChapter[]>("GET", `/books/${id}/recap?bookmark=${bookmark}`);

  entities(id: string, bookmark: number, q?: string) {
    const query = new URLSearchParams({ bookmark: String(bookmark) });
    if (q) query.set("q", q);
    return this.request<Entity[]>("GET", `/books/${id}/entities?${query}`);
  }

  entity = (id: string, entityId: number, bookmark: number) =>
    this.request<EntityDetail>("GET", `/books/${id}/entities/${entityId}?bookmark=${bookmark}`);

  story = (id: string, bookmark: number) =>
    this.request<StorySoFar>("GET", `/books/${id}/story?bookmark=${bookmark}`);

  events = (id: string, bookmark: number) =>
    this.request<BookEvent[]>("GET", `/books/${id}/events?bookmark=${bookmark}`);

  review = (id: string, bookmark: number) =>
    this.request<ReviewItem[]>("GET", `/books/${id}/review?bookmark=${bookmark}`);

  editItem = (
    id: string,
    type: ReviewItem["item_type"],
    itemId: number,
    body: { status?: "verified" | "hidden"; text?: string; title?: string; description?: string },
  ) => this.request<unknown>("PATCH", `/books/${id}/items/${type}/${itemId}`, body);

  renameEntity = (id: string, entityId: number, name: string) =>
    this.request<unknown>("PATCH", `/books/${id}/entities/${entityId}`, { name });

  updateEntity = (
    id: string,
    entityId: number,
    body: { name?: string; entity_type?: EntityType; hidden?: boolean },
  ) => this.request<unknown>("PATCH", `/books/${id}/entities/${entityId}`, body);

  reviewEntities = (id: string, bookmark: number) =>
    this.request<ReviewEntity[]>("GET", `/books/${id}/review/entities?bookmark=${bookmark}`);

  editLog = (id: string) => this.request<EditLogEntry[]>("GET", `/books/${id}/edits`);

  private wiki<T>(bookId: string, path: string, bookmark: number, extra = "") {
    return this.request<T>(
      "GET",
      `/books/${bookId}/wiki${path}?bookmark=${bookmark}${extra}`,
      undefined,
      this.wikiBase,
    );
  }
  wikiPortal = (id: string, bm: number) => this.wiki<Portal>(id, "", bm);
  wikiArticle = (id: string, entityId: number, bm: number) =>
    this.wiki<Article>(id, `/articles/${entityId}`, bm);
  wikiChapter = (id: string, chapter: number, bm: number) =>
    this.wiki<ChapterPage>(id, `/chapters/${chapter}`, bm);
  wikiCategory = (id: string, type: string, bm: number) =>
    this.wiki<Category>(id, `/category/${type}`, bm);
  wikiTimeline = (id: string, bm: number) => this.wiki<Timeline>(id, "/timeline", bm);
  wikiGraph = (id: string, bm: number) => this.wiki<Graph>(id, "/graph", bm);
  wikiChanges = (id: string, since: number, bm: number) =>
    this.wiki<Changes>(id, "/changes", bm, `&since=${since}`);

  async wikiExport(bookId: string, bookmark: number): Promise<Blob> {
    const r = await fetch(`${this.wikiBase}/books/${bookId}/wiki/export?bookmark=${bookmark}`, {
      headers: { Authorization: `Bearer ${await this.token()}` },
    });
    if (!r.ok) throw new ApiError(r.status, "export mislukt");
    return r.blob();
  }

  adminUsers = (status?: string) =>
    this.request<AdminUser[]>("GET", `/admin/users${status ? `?status=${status}` : ""}`);
  approve = (userId: string, credits: number) =>
    this.request<AdminUser>("POST", `/admin/users/${userId}/approve`, { credits });
  grant = (userId: string, credits: number) =>
    this.request<AdminUser>("POST", `/admin/users/${userId}/credits`, { credits });
  block = (userId: string) => this.request<AdminUser>("POST", `/admin/users/${userId}/block`);
  platform = () => this.request<Platform>("GET", "/admin/platform");
  updatePlatform = (body: {
    extraction_enabled?: boolean;
    monthly_budget_usd?: number;
    off_peak_only?: boolean;
    provenance_mode?: PolicyMode;
    public_basis_mode?: PolicyMode;
    country_mode?: Platform["country_mode"];
    allowed_countries?: string[];
  }) =>
    this.request<Platform>("PATCH", "/admin/platform", body);
  notices = (status?: string) =>
    this.request<Notice[]>("GET", `/admin/notices${status ? `?status=${status}` : ""}`);
  decideNotice = (id: string, status: Notice["status"], decision: string) =>
    this.request<Notice>("POST", `/admin/notices/${id}/decision`, { status, decision });
  takedown = (
    id: string,
    body: { book_ids: string[]; reason: string; block_file: boolean; block_isbn: boolean },
  ) => this.request<{ removed: string[] }>("POST", `/admin/notices/${id}/takedown`, body);
  findBooks = (q: string) =>
    this.request<AdminBookHit[]>("GET", `/admin/books?q=${encodeURIComponent(q)}`);
  blocks = () => this.request<ContentBlock[]>("GET", "/admin/blocks");
  addBlock = (body: { kind: ContentBlock["kind"]; value: string; reason?: string }) =>
    this.request<unknown>("POST", "/admin/blocks", body);
  removeBlock = (id: number) => this.request<void>("DELETE", `/admin/blocks/${id}`);
  setRights = (
    bookId: string,
    body: { rights_basis?: Book["rights_basis"]; visibility?: "private" | "public" },
  ) =>
    this.request<{ warnings: string[]; visibility: string; rights_basis: string }>(
      "PATCH",
      `/admin/books/${bookId}/rights`,
      body,
    );
  publicDomainCheckOwn = (bookId: string) =>
    this.request<PublicDomain>("POST", `/books/${bookId}/public-domain`);
  publicDomainCheck = (bookId: string) =>
    this.request<PublicDomain>("POST", `/admin/books/${bookId}/public-domain`);
  usage = () => this.request<UsageRow[]>("GET", "/admin/usage");
  reap = () => this.request<unknown[]>("POST", "/admin/jobs/reap");
}
